<template>
  <div>
    <span class="caption font-weight-medium" v-if="label">
      {{ label }} <span class="error--text" v-if="required">*</span>
    </span>
    <v-autocomplete
      ref="autocomplete"
      v-model="model"
      :prepend-inner-icon="icon"
      :items="items"
      :item-text="itemText"
      :rules="rules"
      :item-value="itemValue"
      :hide-details="hideDetails"
      :readonly="readonly"
      :placeholder="placeholder"
      :return-object="returnObject"
      :multiple="multiple"
      :clearable="clearable"
      :loading="loading"
      :disabled="disabled"
      :chips="chips"
      :dense="dense"
      :deletable-chips="deletableChips"
      :filter="filter || customFilter"
      :hint="hint"
      :persistent-hint="persistentHint"
      :search-input.sync="searchInput"
      :class="color === 'white' ? 'custom-field-white' : 'custom-field'"
      solo
      flat
      filled
      @keyup.enter="onEnter($event)"
      @change="$emit('change')"
    >
      <template v-slot:append-inner>
        <slot name="append-inner"></slot>
      </template>
      <template v-slot:append-outer>
        <slot name="append-outer"></slot>
      </template>
      <template v-slot:prepend-item v-if="prependItem || selectAll">
        <div v-if="selectAll && multiple" class="select-all-item sticky">
          <v-list-item @click="toggleSelectAll" class="select-all-option">
            <v-list-item-action>
              <v-icon @click="toggleSelectAll">mdi-select-all</v-icon>
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title>{{ $t("select_all") }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-divider></v-divider>
        </div>
        <slot name="prepend-item" v-if="prependItem"></slot>
      </template>
      <template v-slot:item="{ item }" v-if="showItemSlot">
        <slot name="item" :item="item"></slot>
      </template>
      <template v-slot:selection="data" v-if="showSelectionSlot">
        <slot name="selection" :data="data"></slot>
      </template>
      <template
        v-else-if="multiple && model.length >= labelMinSelected"
        v-slot:selection="{ item, index }"
      >
        {{ !index ? model.length + ` ${$t("items_selected")}` : "" }}
      </template>
      <template v-else-if="useInternalSelectionSlot" v-slot:selection="{ item, attrs }">
        <v-chip
          v-bind="attrs"
          :input-value="true"
          :close="deletableChips && !chipCloseStart"
          small
          @click:close="removeSelection(item)"
        >
          <v-icon
            v-if="deletableChips && chipCloseStart"
            left
            small
            @click.stop="removeSelection(item)"
          >
            mdi-close-circle
          </v-icon>
          {{ getSelectionLabel(item) }}
        </v-chip>
      </template>
    </v-autocomplete>
  </div>
</template>

<script>
import hasModel from "@/mixins/hasModel";
import { readonly } from "vue";

export default {
  mixins: [hasModel],
  data: () => ({
    searchInput: null,
    lastSearch: "",
  }),
  props: {
    items: {
      type: Array,
      required: true,
    },
    itemText: {
      type: String,
      required: false,
      default: "text",
    },
    itemValue: {
      type: String,
      required: false,
      default: "value",
    },
    prependItem: {
      type: Boolean,
      required: false,
      default: false,
    },
    selectAll: {
      type: Boolean,
      required: false,
      default: false,
    },
    label: {
      type: String,
      required: false,
      default: "",
    },
    loading: {
      type: Boolean,
      required: false,
      default: false,
    },
    hint: {
      type: String,
      required: false,
      default: "",
    },
    showItemSlot: {
      type: Boolean,
      required: false,
      default: false,
    },
    showSelectionSlot: {
      type: Boolean,
      required: false,
      default: false,
    },
    color: {
      type: String,
      required: false,
      default: "default",
    },
    readonly: {
      type: Boolean,
      required: false,
      default: false,
    },
    persistentHint: {
      type: Boolean,
      required: false,
      default: false,
    },
    clearable: {
      type: Boolean,
      required: false,
      default: false,
    },
    returnObject: {
      type: Boolean,
      required: false,
      default: false,
    },
    dense: {
      type: Boolean,
      required: false,
      default: true,
    },
    deletableChips: {
      type: Boolean,
      required: false,
      default: true,
    },
    chips: {
      type: Boolean,
      required: false,
      default: false,
    },
    multiple: {
      type: Boolean,
      required: false,
      default: false,
    },
    hideDetails: {
      type: Boolean,
      required: false,
      default: true,
    },
    required: {
      type: Boolean,
      required: false,
      default: false,
    },
    rules: {
      type: Array,
      required: false,
      default: () => [],
    },
    icon: {
      required: false,
      default: undefined,
    },
    hint: {
      type: String,
      required: false,
      default: "",
    },
    placeholder: {
      type: String,
      required: false,
      default: "",
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    labelMinSelected: {
      type: Number,
      required: false,
      default: 5,
    },
    filter: {
      type: Function,
      required: false,
      default: null,
    },
    truncateChips: {
      type: Boolean,
      required: false,
      default: false,
    },
    chipCloseStart: {
      type: Boolean,
      required: false,
      default: false,
    },
    selectionCharLimit: {
      type: Number,
      required: false,
      default: 36,
    },
  },
  computed: {
    isAllSelected() {
      if (!this.multiple || !this.model || !Array.isArray(this.model)) return false;
      return this.model.length === this.items.length;
    },
    isSomeSelected() {
      if (!this.multiple || !this.model || !Array.isArray(this.model)) return false;
      return this.model.length > 0 && this.model.length < this.items.length;
    },
    useInternalSelectionSlot() {
      return this.multiple && this.chips && (this.truncateChips || this.chipCloseStart);
    },
  },
  watch: {
    searchInput(newValue) {
      this.$emit("update:search-input", newValue);
      if (newValue) {
        this.lastSearch = newValue;
      }
    },
  },
  methods: {
    getComparableValue(value) {
      if (value === null || value === undefined) return "";
      if (typeof value === "object") {
        if (this.itemValue && value[this.itemValue] !== undefined) {
          return String(value[this.itemValue]);
        }
        return JSON.stringify(value);
      }
      return String(value);
    },
    getSelectionLabel(item) {
      let label = "";
      if (item && typeof item === "object") {
        label = String(item[this.itemText] ?? item.name ?? item.text ?? "");
      } else {
        label = String(item ?? "");
      }

      if (!this.truncateChips) return label;
      const limit = Math.max(8, Number(this.selectionCharLimit) || 36);
      if (label.length <= limit) return label;
      return `${label.slice(0, limit - 1)}…`;
    },
    removeSelection(item) {
      if (!this.multiple || !Array.isArray(this.model)) return;
      const targetValue = this.getComparableValue(item);
      this.model = this.model.filter(
        (selectedValue) => this.getComparableValue(selectedValue) !== targetValue
      );
      this.$emit("change");
    },
    onEnter() {
      const autocomplete = this.$refs.autocomplete;

      // 1. Check if Vuetify already selected an item during keydown
      const internalValue = autocomplete?.internalValue;
      if (internalValue && typeof internalValue === "object") {
        this.model = internalValue;
        this.$emit("enter", internalValue);
        this.searchInput = "";
        this.lastSearch = "";
        return;
      }

      // 2. Quick-add: filter items with last known search text
      const search = this.searchInput || this.lastSearch || "";
      let filteredItems;

      if (search.trim()) {
        filteredItems = this.items.filter((item) =>
          (this.filter || this.customFilter)(item, search)
        );
      } else {
        filteredItems = autocomplete?.filteredItems || this.items;
      }

      if (filteredItems && filteredItems.length > 0) {
        const firstItem = this.returnObject
          ? filteredItems[0]
          : filteredItems[0][this.itemValue];
        this.model = firstItem;
        this.$emit("enter", firstItem);
        this.searchInput = "";
        this.lastSearch = "";
      }
    },
    toggleSelectAll() {
      if (!this.multiple) return;

      if (this.isAllSelected) {
        // Deselect all
        this.model = [];
      } else {
        // Select all
        if (this.returnObject) {
          this.model = [...this.items];
        } else {
          this.model = this.items.map((item) => item[this.itemValue]);
        }
      }
      this.$emit("change");
    },
    customFilter(item, queryText) {
      return Object.values(item).some((value) => {
        if (typeof value === "string") {
          return this.translate(value)
            .toLowerCase()
            .includes(this.translate(queryText).toLowerCase());
        }
        return false;
      });
    },
  },
};
</script>

<style scoped>
.select-all-item.sticky {
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: white;
  border-bottom: 1px solid #e0e0e0;
}

.select-all-option {
  background-color: #f5f5f5;
}

.select-all-option:hover {
  background-color: #eeeeee;
}
</style>
